/**
 * FAAB ledger.
 *
 * FAAB is the league's real currency — it pays for waivers, the farm auction, the keeper
 * luxury tax, fines, trades, and in-season wagers. Every movement is an entry so the
 * commissioners can reconcile a season line by line.
 *
 * 2018-II-4  — 50% of unspent FAAB rolls over; odd balances round down via (n-1)/2. No cap.
 * 2020-I-6   — $2 real money buys $1 FAAB; funds live within 24 hours of payment.
 * 2022       — no limit on the number of purchases (regular season).
 * 2023       — purchases also allowed during the offseason, credited to the next season.
 * 2014-II-2  — FAAB may be included in trades ("cash considerations").
 * 2019-III-4 — $5 fine per player drafted then dropped before Week 1 (NFL IR exempt).
 * 2020-I-5 §5 — $50 fine for refusing an "Own Your Soul" rename; a deficit rolls forward.
 * 2019-I-1   — an incoming GM whose predecessor traded away FAAB is reset to $100.
 */

import type { LeagueConfig } from './config.js';

export type FaabReason =
  | 'season-allocation'
  | 'rollover'
  | 'purchase'
  | 'waiver-claim'
  | 'farm-auction'
  | 'luxury-tax'
  | 'trade'
  | 'fine-post-draft-drop'
  | 'fine-own-your-soul'
  | 'wager-stake'
  | 'wager-payout'
  | 'commissioner-adjustment';

export interface FaabEntry {
  teamId: string;
  season: number;
  /** Negative for spend, positive for credit. */
  amount: number;
  reason: FaabReason;
  note?: string;
  /** ISO date. */
  at: string;
}

/**
 * 2018-II-4 §1-2: "GMs are eligible to roll over 50% of all remaining FAAB Funds... If the
 * remaining sum is an odd number, the 50% will be calculated as (Remaining Funds - 1) x 50%."
 * A negative balance (an unpaid fine) rolls forward in full — 2020-I-5 §5.
 */
export function rolloverAmount(endingBalance: number, config: LeagueConfig): number {
  if (endingBalance < 0) return endingBalance; // deficits carry in full
  if (!Number.isInteger(endingBalance)) {
    throw new RangeError(`FAAB balances are whole dollars, got ${endingBalance}`);
  }
  const base = endingBalance % 2 === 0 ? endingBalance : endingBalance - 1;
  return base * config.faabRolloverRate;
}

/**
 * The FAAB sign.
 *
 * The league deals in two currencies and the app used one symbol for both, which is how somebody
 * reads "$88 luxury tax" and reaches for a wallet. FAAB is a league scrip — it buys players, it is
 * fined, it is taxed, it rolls over — and only ONE number in the whole app is real money: the rate
 * at which real dollars buy it. So FAAB is ₣ everywhere and $ is reserved for actual currency.
 */
export const FAAB = '\u20a3';
export const faab = (n: number): string => `${FAAB}${n}`;

/** 2020-I-6 §2: $2 of real money buys ₣1 of FAAB. */
export function faabFromRealMoney(realDollars: number, config: LeagueConfig): number {
  return Math.floor(realDollars / config.faabPurchaseRate);
}

export function realMoneyForFaab(faabDollars: number, config: LeagueConfig): number {
  return faabDollars * config.faabPurchaseRate;
}

export function balance(entries: readonly FaabEntry[], teamId: string, season: number): number {
  return entries
    .filter((e) => e.teamId === teamId && e.season === season)
    .reduce((sum, e) => sum + e.amount, 0);
}

/**
 * Open a team's ledger for a new season: base allocation plus whatever rolled over.
 * 2019-I-1: an incoming GM is reset to the standard base if his predecessor traded FAAB away,
 * but he keeps any rollover, and if he somehow arrives holding more than the base he keeps it.
 */
export function openSeason(
  teamId: string,
  season: number,
  priorEndingBalance: number,
  config: LeagueConfig,
  opts: { newGM?: boolean; at?: string } = {},
): FaabEntry[] {
  const at = opts.at ?? `${season}-01-01T00:00:00Z`;
  const rollover = rolloverAmount(priorEndingBalance, config);
  const entries: FaabEntry[] = [
    {
      teamId,
      season,
      amount: config.faabBase,
      reason: 'season-allocation',
      note: `Base FAAB allocation for ${season}`,
      at,
    },
  ];
  if (rollover !== 0) {
    entries.push({
      teamId,
      season,
      amount: rollover,
      reason: 'rollover',
      note:
        rollover < 0
          ? `Deficit carried forward from ${season - 1} (2020-I-5 §5)`
          : `50% rollover of ${FAAB}${priorEndingBalance} unspent in ${season - 1} (2018-II-4)`,
      at,
    });
  }
  if (opts.newGM) {
    entries.push({
      teamId,
      season,
      amount: 0,
      reason: 'commissioner-adjustment',
      note: 'Incoming GM: FAAB reset to standard base per 2019-I-1 §1 if predecessor traded funds away.',
      at,
    });
  }
  return entries;
}

/**
 * In-season wagering, 2023 "In-Season Wagering On Fantasy Matchups".
 * Week 9 onward, upsets only, one bet per week, stake capped at $100 FAAB.
 * Payout ladder is by seed differential.
 */
/** 2023 in-season wagering §3: American odds by seed gap. Exported so Home can show the board. */
export const WAGER_ODDS: Readonly<Record<number, number>> = {
  1: 50,
  2: 100,
  3: 150,
  4: 200,
  5: 250,
  6: 300,
  7: 350,
  8: 400,
  9: 450,
};

export interface WagerValidation {
  legal: boolean;
  errors: string[];
  /** American odds, e.g. +250. */
  americanOdds: number | null;
  /** FAAB returned on a win, excluding the returned stake. */
  profitFaab: number | null;
}

export function validateWager(input: {
  week: number;
  /** Standings rank of the underdog at kickoff (higher number = worse seed). */
  underdogSeed: number;
  /** Standings rank of the favorite at kickoff. */
  favoriteSeed: number;
  stakeFaab: number;
  bettorIsInMatchup: boolean;
  existingBetsThisWeek: number;
}): WagerValidation {
  const errors: string[] = [];
  if (input.week < 9) errors.push('Wagering opens in Week 9 (2023 §1).');
  if (input.bettorIsInMatchup) errors.push('A GM may not bet on his own matchup (2023 §1).');
  if (input.existingBetsThisWeek >= 1) errors.push('One wager per GM per week (2023 §6).');
  if (input.stakeFaab > 100) errors.push('Stake may not exceed $100 FAAB (2023 §5).');
  if (input.stakeFaab <= 0) errors.push('Stake must be positive.');
  if (input.underdogSeed <= input.favoriteSeed) {
    errors.push(
      'Wagers may only be placed on an upset — the lower-ranked team beating the higher-ranked ' +
        'team (2023 §2).',
    );
  }

  const diff = input.underdogSeed - input.favoriteSeed;
  const odds = WAGER_ODDS[diff] ?? null;
  if (odds === null && errors.length === 0) {
    errors.push(`No published odds for a seed differential of ${diff} (2023 §3).`);
  }

  return {
    legal: errors.length === 0,
    errors,
    americanOdds: odds,
    profitFaab: odds === null ? null : Math.round((input.stakeFaab * odds) / 100),
  };
}

/* -- what everybody starts 2026 with ------------------------------------------------------- */

import openingData from '../data/faab-2026.json';

interface OpeningFile {
  source: string; season: number; priorSeason: number; base: number;
  endingBalances: Record<string, number>;
}
const OPENING = openingData as unknown as OpeningFile;

export interface OpeningBalance {
  gm: string;
  /** What he finished the previous season holding. */
  ended: number;
  /** Half of it, fractions dropped (2018-II-4 §1). */
  rolled: number;
  /** Rollover plus the season's base allotment — what the ledger starts from. */
  start: number;
}

/**
 * The 2026 opening balances.
 *
 * Not everybody starts level, and the app used to pretend they did: it seeded every GM at the
 * ₣100 base, which quietly overstated Luke by ₣39 and understated Samant by ₣40 before a single
 * transaction was recorded. Half of last season's leftovers carries, fractions dropped — so
 * Rajan's ₣13 becomes ₣6, not ₣6.50.
 */
export function openingBalances(): OpeningBalance[] {
  return Object.entries(OPENING.endingBalances)
    .map(([gm, ended]) => {
      const rolled = ended < 0 ? ended : Math.floor(ended / 2);
      return { gm, ended, rolled, start: rolled + OPENING.base };
    })
    .sort((a, b) => b.start - a.start);
}

/** Just the number, for seeding a ledger. Falls back to the base for anybody with no history. */
export function openingBalance(gm: string): number {
  const hit = openingBalances().find((x) => x.gm === gm.toUpperCase());
  return hit ? hit.start : OPENING.base;
}

/**
 * How each manager's season opens, written as ledger lines.
 *
 * A ledger that begins at ₣140 and does not say why is a balance wearing a ledger's clothes. The
 * two facts that produce that number — what he had left in 2025 and what the league gave him —
 * are the ones argued about, so they are entries like any other and the column adds up from zero.
 *
 * They are DERIVED, not stored: the rollover rule is in the rulebook and the ending balances are
 * in one data file, so correcting either reprices every opening rather than leaving a stale row
 * somebody has to remember to edit.
 */
export interface OpeningLine {
  gm: string;
  kind: 'rollover' | 'allotment';
  amount: number;
  description: string;
  date: string;
}

export function openingLedger(gms?: readonly string[]): OpeningLine[] {
  const date = `${OPENING.season}-01-01`;
  const known = new Map(openingBalances().map((b) => [b.gm, b]));
  const who = (gms ?? [...known.keys()]).map((g) => g.toUpperCase());
  const out: OpeningLine[] = [];

  for (const gm of who) {
    const b = known.get(gm);
    if (b && b.rolled !== 0) {
      out.push({
        gm,
        kind: 'rollover',
        amount: b.rolled,
        // The odd dollar simply vanishes on an odd balance, and the league has known that since
        // 2018 — flagging it every year reads as a correction nobody asked for.
        description: b.rolled < 0
          ? `Deficit of ${faab(-b.ended)} carried forward in full from ${OPENING.priorSeason} `
            + '(2020-I-5 §5)'
          : `Half of the ${faab(b.ended)} left at the end of ${OPENING.priorSeason} `
            + '(2018-II-4 §1)',
        date,
      });
    }
    out.push({
      gm,
      kind: 'allotment',
      amount: OPENING.base,
      description: b
        ? `${OPENING.season} base allotment`
        : `${OPENING.season} base allotment — no ${OPENING.priorSeason} balance on file, so nothing rolled over`,
      date,
    });
  }
  return out;
}

export const OPENING_SOURCE = OPENING.source;
export const FAAB_BASE = OPENING.base;
